import { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import moment from 'moment'
import {
  Chip, FormControl,
  InputLabel, MenuItem,
  Select, TableCell,
  TableRow
} from '@mui/material'
import ActionMenu from 'components/actionMenu'
import Filter from 'components/filter/filter'
import CONSTANT from 'modules/constant'
import GLOBAL from 'modules/global'
import {
  useDownloadInjectionTemplateMutation, useGetInjectionJobsMutation
} from 'store/carInjection'
import Content from 'components/content/content'
import UploadDialog from './uploadDialog'
import { JobDetailDrawer } from "./detailInjection"

const jobStatusColors: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  UPLOADED: 'default',
  PROCESSING: 'info',
  COMPLETED: 'success',
  PARTIAL_FAILED: 'warning',
  FAILED: 'error'
}

const jobTableHeadTitles = [
  'ID',
  'File Name',
  'Status',
  'Uploaded By',
  'Uploaded Date',
  'Notes',
  'Details'
]

const formatDate = (e: string) => (!isEmpty(e) ? moment(e).format('DD-MM-YYYY HH:mm') : '-')

const CarInjection = () => {
  const [getInjectionJobs, jobsResult] = useGetInjectionJobsMutation()
  const [downloadTemplate, download] = useDownloadInjectionTemplateMutation()
  const [uploadOpen, setUploadOpen] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null)

  const [filter, setFilter] = useState({ status: '' })

  const [payload, setPayload] = useState({
    start: 0,
    length: CONSTANT.DEFAULT_PAGINATION_PER_PAGE,
    status: ''
  })

  const onChangePage = (value: number) => setPayload({ ...payload, start: value - 1 })
  const onChangePerPage = (value: number) => setPayload({ ...payload, length: value })

  const fetchJobs = () => {
    const formData = new FormData()
    formData.append('start', payload.start.toString())
    formData.append('length', payload.length.toString())
    if (!isEmpty(payload.status)) formData.append('status', payload.status)
    getInjectionJobs(formData)
  }

  useEffect(() => {
    fetchJobs()
  }, [payload])

  const totalPage = jobsResult.data ? GLOBAL.tableTotalPage(jobsResult.data.recordsTotal) : 1
  const numbers = GLOBAL.tableNumber(payload.start)

  return (
    <>
      <Content
        title='Car Injection'
        tableHeadTitles={jobTableHeadTitles}
        onResync={fetchJobs}
        onFilter={() => setFilterOpen(true)}
        pagination={onChangePage}
        onChangePerPage={onChangePerPage}
        totalPage={totalPage}
        totalRecords={jobsResult.data?.recordsTotal}
        page={payload.start + 1}
        isLoading={jobsResult.isLoading}
        downloadTitle="Download Template"
        downloadLoading={download.isLoading}
        onDownload={() => downloadTemplate()}
        uploadTitle="Upload File"
        onUpload={() => setUploadOpen(true)}
      >
        {jobsResult.isSuccess &&
          jobsResult.data?.data?.map((job, index) => (
            <TableRow key={job.id} hover>
              <TableCell>{numbers[index] + 1}</TableCell>
              <TableCell>
                <ActionMenu
                  id={`job-${job.jobId}`}
                  menus={[{ label: 'View', onClick: () => setSelectedJobId(job.jobId) }]}
                />
              </TableCell>
              <TableCell>{job.jobId?.slice(0, 12)}...</TableCell>
              <TableCell>{job.fileName || job.sourceFilename || '-'}</TableCell>
              <TableCell>
                <Chip
                  label={job.status}
                  color={jobStatusColors[job.status] || 'default'}
                  size='small'
                />
              </TableCell>
              <TableCell>{job.uploadedBy || job.createdBy || '-'}</TableCell>
              <TableCell>{formatDate(job.uploadedDate || job.createdAt)}</TableCell>
              <TableCell>{job.notes || '-'}</TableCell>
              <TableCell>{`${job.successRows ?? 0}/${job.totalRows ?? 0}`}</TableCell>
            </TableRow>
          ))}
      </Content>

      <Filter
        open={filterOpen}
        title='Filter'
        onClose={() => setFilterOpen(false)}
        onCancel={() => setFilterOpen(false)}
        onSubmit={() => {
          setPayload({ ...payload, ...filter, start: 0 })
          setFilterOpen(false)
        }}
        isSubmitDisabled={!GLOBAL.isFilterContainValue(filter)}
        onReset={() => {
          setFilter({ status: '' })
          setPayload({ ...payload, start: 0, status: '' })
          setFilterOpen(false)
        }}
      >
        <FormControl fullWidth>
          <InputLabel id='status-label'>Status</InputLabel>
          <Select
            labelId='status-label'
            value={filter.status}
            label='Status'
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          >
            <MenuItem value='UPLOADED'>UPLOADED</MenuItem>
            <MenuItem value='PROCESSING'>PROCESSING</MenuItem>
            <MenuItem value='COMPLETED'>COMPLETED</MenuItem>
            <MenuItem value='PARTIAL_FAILED'>PARTIAL_FAILED</MenuItem>
            <MenuItem value='FAILED'>FAILED</MenuItem>
          </Select>
        </FormControl>
      </Filter>

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSuccess={(jobId) => setSelectedJobId(jobId)}
        onUploadComplete={fetchJobs}
      />

      {selectedJobId && (
        <JobDetailDrawer jobId={selectedJobId} onClose={() => setSelectedJobId(null)} />
      )}
    </>
  )
}

export default CarInjection
